import apiClient from "./apiClient"
import { UnknownRecord } from "../models/api"
import { CashMovementPayload } from "../models/pos"

type ListOptions = {
    limit?: number
    offset?: number
}

type ListByRegisterOptions = ListOptions & {
    status?: string
}

type CashMovementListOptions = ListOptions & {
    startDate?: string
    endDate?: string
}

type CashMovementPagination = {
    limit: number
    offset: number
    hasMore: boolean
}

type CashMovementListResponse = {
    movements: UnknownRecord[]
    pagination: CashMovementPagination
}

class CashSessionService {
    async openSession(
        companyId: string,
        data: UnknownRecord
    ): Promise<UnknownRecord> {
        const payload: UnknownRecord = { ...(data || {}) }
        if (
            payload.openingFloatMinor == null &&
            Number.isInteger(payload.opening_float_minor)
        ) {
            payload.openingFloatMinor = payload.opening_float_minor
        }

        const response = await apiClient.post(
            `/api/companies/${companyId}/cash-sessions`,
            payload
        )
        return (response as UnknownRecord).session as UnknownRecord
    }

    async getPosContext(companyId: string): Promise<UnknownRecord> {
        return await apiClient.get(`/api/companies/${companyId}/pos/context`)
    }

    async getSession(
        companyId: string,
        sessionId: string
    ): Promise<UnknownRecord> {
        const response = await apiClient.get(
            `/api/companies/${companyId}/cash-sessions/${sessionId}`
        )
        return (response as UnknownRecord).session as UnknownRecord
    }

    async getOpenSession(
        companyId: string,
        registerId: string
    ): Promise<UnknownRecord> {
        const response = await apiClient.get(
            `/api/companies/${companyId}/cash-sessions/open?registerId=${registerId}`
        )
        return (response as UnknownRecord).session as UnknownRecord
    }

    async listSessions(
        companyId: string,
        outletId: string,
        options: ListOptions = {}
    ): Promise<UnknownRecord[]> {
        const { limit = 50, offset = 0 } = options
        const response = await apiClient.get(
            `/api/companies/${companyId}/cash-sessions?outletId=${outletId}&limit=${limit}&offset=${offset}`
        )
        return ((response as UnknownRecord).sessions as UnknownRecord[]) || []
    }

    async listSessionsByRegister(
        companyId: string,
        registerId: string,
        options: ListByRegisterOptions = {}
    ): Promise<UnknownRecord[]> {
        const { status, limit = 50, offset = 0 } = options

        let url = `/api/companies/${companyId}/cash-sessions?registerId=${registerId}&limit=${limit}&offset=${offset}`
        if (status) {
            url += `&status=${encodeURIComponent(status)}`
        }

        const response = await apiClient.get(url)
        return ((response as UnknownRecord).sessions as UnknownRecord[]) || []
    }

    async getLatestClosedSessionByRegister(
        companyId: string,
        registerId: string
    ): Promise<UnknownRecord | null> {
        const sessions = await this.listSessionsByRegister(
            companyId,
            registerId,
            {
                status: "closed",
                limit: 1,
                offset: 0,
            }
        )
        return sessions[0] || null
    }

    async closeSession(
        companyId: string,
        sessionId: string,
        data: UnknownRecord
    ): Promise<UnknownRecord> {
        const response = await apiClient.post(
            `/api/companies/${companyId}/cash-sessions/${sessionId}/close`,
            data
        )
        return response
    }

    async closeSessionTheoretical(
        companyId: string,
        sessionId: string
    ): Promise<UnknownRecord> {
        const response = await apiClient.post(
            `/api/companies/${companyId}/cash-sessions/${sessionId}/close-theoretical`,
            {}
        )
        return response
    }

    async timeoutSession(
        companyId: string,
        sessionId: string
    ): Promise<UnknownRecord> {
        const response = await apiClient.post(
            `/api/companies/${companyId}/cash-sessions/${sessionId}/timeout`,
            {}
        )
        return response
    }

    async getSessionMovements(
        companyId: string,
        sessionId: string,
        options: CashMovementListOptions = {}
    ): Promise<CashMovementListResponse> {
        const { startDate, endDate, limit, offset } = options
        const params = new URLSearchParams()
        if (Number.isInteger(limit as number) && Number(limit) > 0) {
            params.set("limit", String(limit))
        }
        if (Number.isInteger(offset as number) && Number(offset) >= 0) {
            params.set("offset", String(offset))
        }
        let url = `/api/companies/${companyId}/cash-sessions/${sessionId}/movements`

        if (startDate) {
            params.set("startDate", startDate)
        }
        if (endDate) {
            params.set("endDate", endDate)
        }

        const query = params.toString()
        if (query) {
            url += `?${query}`
        }

        const response = (await apiClient.get(url)) as UnknownRecord
        const movements = (response.movements as UnknownRecord[]) || []
        const pagination =
            (response.pagination as CashMovementPagination | undefined) ||
            ({
                limit:
                    Number.isInteger(limit as number) && Number(limit) > 0
                        ? Number(limit)
                        : movements.length,
                offset:
                    Number.isInteger(offset as number) && Number(offset) >= 0
                        ? Number(offset)
                        : 0,
                hasMore: false,
            } as CashMovementPagination)

        return { movements, pagination }
    }

    async recordCashMovement(
        companyId: string,
        data: CashMovementPayload
    ): Promise<UnknownRecord> {
        const response = await apiClient.post(
            `/api/companies/${companyId}/cash-movements`,
            data
        )
        return (response as UnknownRecord).movement as UnknownRecord
    }

    async listCashMovements(
        companyId: string,
        registerId: string,
        options: CashMovementListOptions = {}
    ): Promise<CashMovementListResponse> {
        const { startDate, endDate, limit = 100, offset = 0 } = options
        let url = `/api/companies/${companyId}/cash-movements?registerId=${registerId}&limit=${limit}&offset=${offset}`

        if (startDate) {
            url += `&startDate=${encodeURIComponent(startDate)}`
        }
        if (endDate) {
            url += `&endDate=${encodeURIComponent(endDate)}`
        }

        const response = (await apiClient.get(url)) as UnknownRecord
        const movements = (response.movements as UnknownRecord[]) || []
        const pagination =
            (response.pagination as CashMovementPagination | undefined) ||
            ({
                limit,
                offset,
                hasMore: false,
            } as CashMovementPagination)

        return { movements, pagination }
    }

    async listMovementsBySession(
        companyId: string,
        sessionId: string,
        options: CashMovementListOptions = {}
    ): Promise<UnknownRecord[]> {
        const { movements } = await this.getSessionMovements(
            companyId,
            sessionId,
            options
        )
        return movements
    }

    async getZReport(
        companyId: string,
        cashSessionId: string
    ): Promise<UnknownRecord> {
        const response = await apiClient.get(
            `/api/companies/${companyId}/reports/z-report?cashSessionId=${cashSessionId}`
        )
        return (response as UnknownRecord).report as UnknownRecord
    }
}

export default new CashSessionService()
